import { SPLIT_METRIC_MEASURES, Entities, Periods } from 'cio/utils/metrics/common';
import { SplitMetric } from 'cio/utils/metrics/models/split-metric';
import moment from 'moment-timezone';
import { ONE_HOUR_IN_SECONDS, ONE_DAY_IN_SECONDS, ONE_WEEK_IN_SECONDS } from 'cio/utils/time-constants';

// TODO: WIP type, hard-coded SPLIT_METRIC_MEASURES
export type SplitMetricResult = {
  id: string;
  action_id: string;
  campaign_id: string;
  period: Periods;

  // hard-coded keys for now, should be indexed by SPLIT_METRIC_MEASURES
  sent: number[];
  opened: number[];
  clicked: number[];
  converted: number[];
};

export function deserializeCampaignSplitMetrics(
  data: { split_metrics: Record<string, SplitMetricResult[]> },
  _campaignId: number | string,
  meta?: { environmentId?: number | string; campaignId?: number | string },
): null | Record<string, null | SplitMetric[]> {
  if (!data.split_metrics) {
    return null;
  }

  let apiData = data.split_metrics;

  return Object.keys(apiData).reduce<Record<string, null | SplitMetric[]>>((acc, id) => {
    acc[id] = deserialize({ split_metrics: apiData[id] }, id, meta);
    return acc;
  }, {});
}

export function deserialize(
  data: { split_metrics: SplitMetricResult[] },
  entityId: number | string,
  meta?: { environmentId?: number | string; campaignId?: number | string },
): null | SplitMetric[] {
  if (!data.split_metrics) {
    return null;
  }

  return data.split_metrics.map((result) => {
    let processedData: Record<string, any> = {};

    let sentLength = result.sent.length;
    let timestamps: number[] = [];

    if (result.period === Periods.HOURS) {
      let endTimestamp = moment.tz('Etc/GMT+4').startOf('hour').unix();

      timestamps = [...new Array(sentLength)].map((_, i) => endTimestamp - i * ONE_HOUR_IN_SECONDS).reverse();
    } else if (result.period === Periods.MONTHS) {
      let endMoment = moment.tz('Etc/GMT+4').startOf('month');

      timestamps = [...new Array(sentLength)]
        .map((_, i) =>
          endMoment
            .clone() // necessary to avoid mutating endMoment object
            .subtract(i, 'month') // months can be 28/29/30/31 days long: Moment magic to always arrive at first day of the month
            .unix(),
        )
        .reverse();
    } else if (result.period === Periods.WEEKS) {
      let endMomentTimestamp = moment.tz('Etc/GMT+4').startOf('week').unix();

      timestamps = [...new Array(sentLength)].map((_, i) => endMomentTimestamp - i * ONE_WEEK_IN_SECONDS).reverse();
    } else {
      // default to "days" period:
      let endTimestamp = moment.tz('Etc/GMT+4').startOf('day').unix();

      timestamps = [...new Array(sentLength)].map((_, i) => endTimestamp - i * ONE_DAY_IN_SECONDS).reverse();
    }

    SPLIT_METRIC_MEASURES.forEach((measure) => {
      // @ts-ignore - not fully typed
      let data: number[] = result[measure] ?? [];

      let measureData = timestamps.map((timestamp, i) => ({
        x: timestamp * 1000,
        y: data[i],
      }));

      processedData[measure] = measureData;
    });

    return new SplitMetric({
      entityId,
      entityType: Entities.ACTION,
      range: {
        start: moment.unix(timestamps[0]),
        end: moment.unix(timestamps[timestamps.length - 1]),
      },
      data: result,
      measureData: processedData,
      meta,
    });
  });
}
